import type { ReactNode } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useLocale } from "@/context/LanguageContext";
import type { AnalyticsTimeSeriesPoint } from "@/graphql/generated";
import { ChartCard, ChartEmpty, ChartSkeleton } from "./ChartCard";
import { formatCompactNumber, formatShortDate } from "../utils/dateRange";

interface Props {
  data: AnalyticsTimeSeriesPoint[];
  loading?: boolean;
  action?: ReactNode;
}

export function AnalyticsTimeSeriesChart({ data, loading = false, action }: Props) {
  const { t } = useLocale();
  const title = t("analytics.timeSeries.title");

  if (loading && data.length === 0) {
    return (
      <ChartCard title={title} action={action}>
        <ChartSkeleton />
      </ChartCard>
    );
  }
  if (data.length === 0) {
    return (
      <ChartCard title={title} action={action}>
        <ChartEmpty />
      </ChartCard>
    );
  }

  const anySuppressed = data.some((p) => p._suppressed);
  // Suppressed buckets become `null` so the line gaps (connectNulls={false}).
  const chartData = data.map((p) => ({
    label: formatShortDate(p.period),
    count: p._suppressed ? null : p.count ?? 0,
  }));

  return (
    <ChartCard title={title} action={action}>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis
            dataKey="label"
            fontSize={11}
            tickLine={false}
            axisLine={false}
            tick={{ fill: "var(--color-muted-foreground)" }}
          />
          <YAxis
            fontSize={11}
            tickLine={false}
            axisLine={false}
            tick={{ fill: "var(--color-muted-foreground)" }}
            tickFormatter={formatCompactNumber}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "var(--color-card)",
              border: "1px solid var(--color-border)",
              borderRadius: "8px",
              color: "var(--color-foreground)",
              fontSize: 12,
            }}
            formatter={(value) => [Number(value).toLocaleString(), t("analytics.timeSeries.tooltip")]}
          />
          <Line
            type="monotone"
            dataKey="count"
            name={t("analytics.timeSeries.tooltip")}
            stroke="var(--color-primary)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0, fill: "var(--color-primary)" }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
      {anySuppressed && (
        <p className="mt-3 text-xs text-muted-foreground">{t("analytics.suppressionNote")}</p>
      )}
    </ChartCard>
  );
}
